<template>
    <div>
        <loading-spinner :show-loading="showUploadingSpinner"></loading-spinner>
        <VueFileAgent v-model="fileRecords" ref="vueFileAgent" :theme="'list'" :multiple="true" :deletable="true"
            :sortable="false" :resumable="false" :auto="false" :meta="true" :accept="permittedFileTypeString"
            :maxSize="maxSize" :maxFiles="20" :helpText="'Select or drop file(s) here'"
            errorText="We're sorry, but we were unable to upload your file. Please ensure that the file type is a .csv file and that it is less than 10MB in size. If you continue to experience issues, please contact your administrator for further assistance."
            :fileCheckUrl="url" @select="filesSelected($event)" @beforedelete="onBeforeDelete($event)"
            @delete="fileDeleted($event)">
        </VueFileAgent>
        <component :is="component" v-model="data"></component>
        <div class="mt-2">
            <div v-if="isAdminOnly">
                <label for="adminOnly" class="form-check-label">Hide From Standard User</label>
                <input id="adminOnly" type="checkbox" class="form-check-input ms-2" checked="false"
                    v-model="hideFromStdUser" />
            </div>
            <div class="alert alert-dismissable alert-warning" v-if="fileRecords.length == 20">
                You can only upload 20 files at a time. Please do another upload for the additional files.
            </div>
            <div class="mt-2">
                <button class="btn btn-primary" :disabled="isUploadDisabled || showUploadingSpinner"
                    @click="uploadFiles($event)" type="button">
                    Upload {{ fileRecords.length }} file(s)
                </button>
            </div>
        </div>
        <div ref="screenDetector" style="height: 0; width: 0;" class="d-none d-sm-block"></div>
    </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref, PropType } from "vue";
import VueFileAgent from "./FileAgent/components/vue-file-agent.vue";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import { FileRecord } from "./FileAgent";
import { Options, RawFileRecord } from "./FileAgent/lib/file-record";

import PopulationUpload from "./PopulationUpload.vue";
import { UploadType } from "@/types";
import axios from "axios";

export default defineComponent({
    components: {
        VueFileAgent,
        LoadingSpinner,
        PopulationUpload,
    },
    setup(props) {
        const vueFileAgent = ref<typeof VueFileAgent>();
        const fileRecords = ref([] as RawFileRecord[]);
        const hideFromStdUser = ref(false);
        const fileRecordsForUpload = ref([] as FileRecord[]);
        const showUploadingSpinner = ref(false);
        const data = ref();
        return {
            vueFileAgent,
            fileRecords,
            hideFromStdUser,
            fileRecordsForUpload,
            showUploadingSpinner,
            data,
        };
    },
    props: {
        label: String,
        url: String,
        permittedFileType: Array as PropType<String[]>,
        isAdminOnly: Boolean,
        dataset: String,
        maxSize: String,
    },
    computed: {
        component(): string | null {
            let uploadType = UploadType[this.$props.dataset!];
            if (uploadType == UploadType.population) {
                return "population-upload";
            } else {
                return null;
            }
        },
        uploadHeaders(): any {
            return {
                adminOnly: this.hideFromStdUser,
            };
        },
        permittedFileTypeString(): string | undefined {
            if (
                this.permittedFileType === undefined ||
                this.permittedFileType[0] === "*"
            ) {
                return undefined;
            }

            return this.permittedFileType?.join() ?? "";
        },
        isUploadDisabled(): boolean {
            let hasErrors = false;

            this.fileRecords.forEach((f) => {
                if (f.error) {
                    hasErrors = true;
                }
            });

            return this.fileRecords.length <= 0 || hasErrors;
        },
    },
    methods: {
        uploadFiles: function (event) {
            if (event) event.preventDefault();
            this.showUploadingSpinner = true;

            // construct form data
            const formData = new FormData();

            for (let fileRecord of this.fileRecordsForUpload) {

                formData.append(
                    "files",
                    fileRecord.file,
                    encodeURI(fileRecord.name())
                );

                if (this.data) {
                    for (const [key, value] of Object.entries(this.data)) {
                        if (value) {
                            formData.append(key, <string>value);
                        }
                    }
                }
            }
            // make a POST request with Axios
            axios.post(this.url ?? "", formData, {
                headers: this.uploadHeaders,
            }).then(function (response) {
                console.info("Files successfully uploaded", response);
            })
                .catch((err) => {
                    let message =
                        typeof err.response !== "undefined"
                            ? err.response.data.message
                            : err.message;
                    console.warn("error", message);
                })
                .finally(() => {
                    this.fileRecords = [];
                    this.fileRecordsForUpload = [];
                    this.showUploadingSpinner = false;

                    this.$emit("filesuploaded", event);
                });

        },
        deleteUploadedFile: function (fileRecord) {
            // Using the default uploader. You may use another uploader instead.
            this.vueFileAgent?.deleteUpload(
                this.url,
                this.uploadHeaders,
                fileRecord
            );
        },
        filesSelected: function (fileRecordsNewlySelected) {
            var validFileRecords = fileRecordsNewlySelected.filter(
                (fileRecord) => !fileRecord.error
            );

            this.fileRecordsForUpload =
                this.fileRecordsForUpload.concat(validFileRecords);
        },
        fileDeleted: function (fileRecord: FileRecord | RawFileRecord) {
            this.deleteUploadedFile(fileRecord);
        },
        onBeforeDelete: function (fileRecord: any) {
            let uploadIndex = this.fileRecordsForUpload.indexOf(fileRecord);

            if (uploadIndex === -1) {
                let options = {} as Options;
                let file = new FileRecord(fileRecord, options);
                uploadIndex = this.fileRecordsForUpload.indexOf(file);
            }

            if (uploadIndex !== -1) {
                this.fileRecordsForUpload.splice(uploadIndex, 1);
            }

            this.vueFileAgent?.deleteFileRecord(fileRecord); // will trigger 'delete' event
        },
    },
});
</script>